import 'dc/dist/style/dc.min.css';
import './AVIDashboard.scss';
import '../AVIChartCommon.scss';

import reductio from 'reductio';

import React, { Component } from 'react';

import { AVIDashboardDimContext } from './AVIDashboardDimContext';
import AVIFactTileFlex from './AVIFactTileFlex';
import { BaseMixin } from './../Mixins/BaseMixin';
import { NumUtils } from '../ChartUtils/NumUtils';
import PropTypes from 'prop-types';
import ReactToPrint from 'react-to-print';
import { TimeUtils } from './../ChartUtils/TimeUtils';
import * as dc from 'dc';
import { formatDefaultLocale } from 'd3-format';
import { ArticlePopup } from '../../ui-components/ArticlePopup/ArticlePopup';
import withRouter from '../../hocs/withRouter';

class AVIDashboardBase extends Component {
  static propTypes = {
    spacing: PropTypes.number,
    colorScheme: PropTypes.array,
    location: PropTypes.object,
    children: PropTypes.node,
    title: PropTypes.string,
    desc: PropTypes.string,
    group: PropTypes.object,
    units: PropTypes.string,
    type: PropTypes.oneOf(['sum', 'count']),
    formatNumber: PropTypes.func,
    html: PropTypes.object,
    keyName: PropTypes.string,
    useFlex: PropTypes.bool,
    cmsSlug: PropTypes.string
  };

  static defaultProps = {
    spacing: 20,
    colorScheme: [
      '#a6cee3',
      '#1f78b4',
      '#b2df8a',
      '#33a02c',
      '#fb9a99',
      '#e31a1c',
      '#fdbf6f',
      '#ff7f00',
      '#cab2d6',
      '#6a3d9a',
      '#ffff99',
      '#b15928'],
    keyName: null,
    type: 'count',
    formatNumber: NumUtils.integer,
    html: {
      some: '%number',
      one: '%number',
      none: '-'
    },
    units: 'rader',
    useFlex: false,
    cmsSlug: null
  };

  constructor(props) {
    super(props);
    const { spacing, colorScheme } = props;
    this.filterRef = React.createRef();
    this.chartRef = React.createRef();

    this.state = {
      height: window.innerHeight,
      width: window.innerWidth,
      spacing: spacing,
      showArticle: false
    };

    formatDefaultLocale(NumUtils.defaultLocaleJson);

    // Set color scheme
    dc.config.defaultColors(colorScheme);

    // Deregister all charts from dc on open dashboard
    dc.deregisterAllCharts();

    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.alterSelection = this.alterSelection.bind(this);
    this.loopFilters = this.loopFilters.bind(this);
    this.clearAllFilters = this.clearAllFilters.bind(this);
    this.closeArticle = this.closeArticle.bind(this);
  }

  componentDidMount() {
    const { formatNumber, html, group, keyName, type } = this.props;

    // Add window resize listener
    window.addEventListener('resize', this.updateWindowDimensions);

    // Setup counter chart
    if (group) {
      this.chart = dc.numberDisplay(this.chartRef.current);
      BaseMixin(this.chart, this.props);

      // Setup calculation of counter
      let calcData;
      var valueAccessor;

      if (type === 'count') {
        calcData = reductio().count(true);
        valueAccessor = d => d.count;
      } else if (type === 'sum' && keyName) {
        calcData = reductio().sum(d => d[keyName]);
        valueAccessor = d => d.sum;
      }
      if (calcData) {
        calcData(group);
      }

      this.chart
        .group(group)
        .valueAccessor(valueAccessor)
        .formatNumber(formatNumber)
        .html(html)
        .on('postRedraw', this.loopFilters)
        .render();
    }
    // Calculate initial filters
    this.loopFilters();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    if (this.chart) {
      this.chart.on('postRedraw', null);
    }
  }

  shortenDesc(s, l = 300) {
    return s.substring(0, l) + (s.length > l ? '...' : '');
  }

  loopFilters(c, f) {
    var flt = '';
    dc.chartRegistry.list().forEach(ch => {
      if (typeof ch.hasFilter === 'function' && ch.hasFilter()) {
        var filters = ch.filters();
        filters = filters.map(d => {
          if (d.filterType === 'RangedFilter' && d.length > 0) {
            var newRangeFilter = [];
            for (var filterPart = 0; filterPart < d.length; filterPart++) {
              if (TimeUtils.isDate(d[filterPart])) {
                var parsedDate = new Date(d[filterPart]);
                var options = {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                };
                newRangeFilter.push(parsedDate.toLocaleString('nb-NO', options));
              } else {
                newRangeFilter.push(d[filterPart]);
              }
            }
            return newRangeFilter.join(' - ');
          }
          return d;
        });
        flt += ch.filterPrefix + ' (' + filters.join(', ') + '), ';
      }
    });
    if (flt) {
      flt = 'Aktive filter: ' + flt;
    } else {
      flt = 'Ingen aktive filter';
    }
    this.filterRef.current.innerText = flt;
  }

  clearAllFilters() {
    var charts = dc.chartRegistry.list();
    for (var i = 0; i < charts.length; ++i) {
      if (typeof charts[i].filterAll === 'function') {
        charts[i].filterAll();
      }
    }
    dc.redrawAll();
  }

  updateWindowDimensions() {
    this.setState({
      height: window.innerHeight,
      width: window.innerWidth
    });
  }

  alterSelection() {
    const { history } = this.props;
    history.goBack();
  }

  closeArticle() {
    this.setState({
      showArticle: false
    });
  }

  render() {
    const { children, title, desc, group, location, units, useFlex, cmsSlug } = this.props;
    const { width, height, spacing, showArticle } = this.state;
    var dim = {
      width: width,
      height: height,
      spacing: spacing
    };

    let aviDashboardStyle = {};
    if (useFlex) {
      aviDashboardStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '20px'
      };
    }

    return (
      <AVIDashboardDimContext.Provider value={dim}>
        <div
          ref={el => (this.componentRef = el)}
          className={[
            'avi-dashboard',
            location && location.state ? location.state.dashboard : ''
          ].join(' ')}
          style={aviDashboardStyle}
        >
          <div className='avi-dashboard-header'>
            <div className='avi-dashboard-title'>
              <h2>{title}</h2>
              <div title={desc} className='description'>
                {this.shortenDesc(desc)}
              </div>
              {cmsSlug && <span><button className='no-print' onClick={(e) => {
                this.setState({
                  showArticle: true
                });
              }}>Les mer...</button>   </span>}
              <ReactToPrint
                trigger={() => <button className='no-print'>Skriv ut denne siden</button>}
                content={() => this.componentRef}
                pageStyle=''
              />
            </div>
            <div className='avi-dashboard-filter-count'>
              <div className='avi-dashboard-filter-container'>
                <div className='avi-dashboard-filter-header'>
                  <h4>Filter</h4>
                </div>
                <div className='avi-dashboard-filter' ref={this.filterRef} />
                <button className='no-print' onClick={this.clearAllFilters}>
                  Tilbakestill alle aktive filtre
                </button>
              </div>
              {group && (
                <AVIFactTileFlex title='Viser' units={units}>
                  <div className='avi-dashboard-counter' ref={this.chartRef} />
                </AVIFactTileFlex>
              )}
            </div>
          </div>
          <span className='avi-clear-both no-print' />
          {children}
          <span className='avi-clear-both no-print' />
          {cmsSlug && (
            <ArticlePopup cmsSlug={cmsSlug} visible={showArticle} onClose={this.closeArticle} />
          )}
        </div>
      </AVIDashboardDimContext.Provider >
    );
  }
}

export const AVIDashboard = withRouter(AVIDashboardBase);
